
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Language, Direction, defaultLanguage, languageConfig, STORAGE_KEY } from './index';
import arTranslations from './translations/ar.json';
import enTranslations from './translations/en.json';

type TranslationsType = typeof arTranslations;

const translations: Record<Language, TranslationsType> = {
    ar: arTranslations,
    en: enTranslations
};

interface LanguageContextType {
    language: Language;
    direction: Direction;
    setLanguage: (lang: Language) => void;
    t: (key: string, variables?: Record<string, any>) => string;
    toggleLanguage: () => void;
    locale: string;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
LanguageContext.displayName = 'IndiaPlaceLanguageContext';

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [language, setLanguageState] = useState<Language>(() => {
        // Get from localStorage or use default
        const stored = localStorage.getItem(STORAGE_KEY);
        return (stored === 'en' || stored === 'ar') ? stored : defaultLanguage;
    });

    const [, startTransition] = React.useTransition();

    const direction = languageConfig[language].dir;

    // Update document direction and lang when language changes
    useEffect(() => {
        document.documentElement.dir = direction;
        document.documentElement.lang = language;
        localStorage.setItem(STORAGE_KEY, language);
    }, [language, direction]);

    const setLanguage = useCallback((lang: Language) => {
        startTransition(() => {
            setLanguageState(lang);
        });
    }, []);

    const toggleLanguage = useCallback(() => {
        startTransition(() => {
            setLanguageState(prev => prev === 'ar' ? 'en' : 'ar');
        });
    }, []);

    // Translation function
    const t = useCallback((key: string, variables?: Record<string, any>): string => {
        const keys = key.split('.');
        let value: any = translations[language];

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                // Return key if translation not found
                console.warn(`Translation not found: ${key}`);
                return key;
            }
        }

        if (typeof value === 'string' && variables) {
            Object.entries(variables).forEach(([k, v]) => {
                value = value.replace(`{{${k}}}`, String(v));
            });
        }

        return typeof value === 'string' ? value : key;
    }, [language]);

    const locale = language === 'ar' ? 'ar-SA' : 'en-SA';

    return (
        <LanguageContext.Provider value={{ language, direction, setLanguage, t, toggleLanguage, locale }}>
            <div dir={direction} className="min-h-screen">
                {children}
            </div>
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

// Shorthand hook for just the translation function
export const useTranslation = () => {
    const { t, language, direction, locale } = useLanguage();
    return { t, language, direction, locale };
};
